import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Intercepteur HTTP qui décode récursivement les entités HTML dans les réponses JSON.
 *
 * Le backend applique un `escapeHtml()` sur les inputs à chaque passage,
 * ce qui provoque un encodage multi-niveaux des données stockées
 * (ex: &amp;amp;amp;quot; au lieu de ").
 *
 * Cet intercepteur inverse ce processus en décodant toutes les chaînes
 * dans les réponses JSON de l'API jusqu'à stabilisation.
 */
@Injectable()
export class DecodeHtmlResponseInterceptor implements HttpInterceptor {

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // N'appliquer le décodage qu'aux réponses de notre API
    if (!req.url.includes(environment.basePath)) {
      return next.handle(req);
    }

    // Ne pas décoder les routes d'impression, car le HTML doit rester échappé (ex: &lt;script&gt;)
    // Sinon le décodage récursif le transforme en <script> exécutable dans bypassSecurityTrustHtml.
    if (req.url.includes('/print/')) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      map((event: HttpEvent<unknown>) => {
        if (event instanceof HttpResponse && event.body != null) {
          // Ne pas décoder les réponses binaires (Blob, ArrayBuffer)
          if (event.body instanceof Blob || event.body instanceof ArrayBuffer) {
            return event;
          }
          return event.clone({ body: this.decodeValue(event.body) });
        }
        return event;
      })
    );
  }

  private decodeValue(value: unknown): unknown {
    if (value === null || value === undefined) {
      return value;
    }

    if (typeof value === 'string') {
      return this.decodeStringDeep(value);
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return value;
    }

    if (Array.isArray(value)) {
      return value.map(item => this.decodeValue(item));
    }

    if (typeof value === 'object') {
      return this.decodeObject(value as Record<string, unknown>);
    }

    return value;
  }

  private decodeObject(obj: Record<string, unknown>): Record<string, unknown> {
    const decoded: Record<string, unknown> = {};
    for (const key of Object.keys(obj)) {
      decoded[key] = this.decodeValue(obj[key]);
    }
    return decoded;
  }

  private decodeStringDeep(encoded: string): string {
    if (!encoded || !encoded.includes('&')) {
      return encoded;
    }

    let result = encoded;
    let previous: string;
    const textarea = document.createElement('textarea');

    do {
      previous = result;
      textarea.innerHTML = result;
      result = textarea.value;
    } while (result !== previous);

    return result;
  }
}
